// ============================================================================
// GAME SIMULATION ENGINE v2
// Uses real season averages as baseline + smart variance
// ============================================================================
import { getPlayerRating } from './playerRatings';
import PLAYER_POSITIONS from './playerPositions';

// ============================================================================
// RANDOM UTILITIES
// ============================================================================
function rand(min, max) { return min + Math.random() * (max - min); }
function randInt(min, max) { return Math.floor(rand(min, max + 1)); }
function clamp(v, lo, hi) { return Math.max(lo, Math.min(hi, v)); }

// Box-Muller normal distribution
function randNormal(mean, sd) {
  const u1 = Math.random(), u2 = Math.random();
  return mean + sd * Math.sqrt(-2 * Math.log(u1)) * Math.cos(2 * Math.PI * u2);
}

// Skewed normal — allows hot nights to be rarer but more extreme
function randSkewNormal(mean, sd, skew = 0) {
  let val = randNormal(0, 1);
  // Apply skew: positive skew = more upside outliers
  val = val + skew * ((val * val - 1) / 2);
  return mean + sd * val;
}

// ============================================================================
// VARIANCE MODEL
// ============================================================================
// The key insight: a star averaging 28 PPG has a tighter relative spread
// than a role player averaging 8 PPG. But absolute variance scales with volume.
//
// Also: OVR acts as a CONSISTENCY modifier.
// A 95 OVR player hits their averages more reliably.
// A 72 OVR player is streakier relative to their (lower) averages.
//
// Real NBA stat distributions are slightly right-skewed for scoring
// (more 40-pt games than 2-pt games for a 25 PPG scorer).

function getVarianceProfile(avgStat, statType, ovr, mpg) {
  // Base coefficient of variation by stat type
  // These represent how "swingy" each stat naturally is
  const cvBase = {
    pts:  0.32,  // Scoring: moderate variance (28 avg → ~9 SD → range ~10-46)
    reb:  0.35,  // Rebounds: moderate-high
    ast:  0.40,  // Assists: high variance (matchup dependent)
    stl:  0.55,  // Steals: very high variance (0-5 range common)
    blk:  0.60,  // Blocks: very high variance
    tov:  0.40,  // Turnovers: moderate-high
    fga:  0.25,  // Shot attempts: more stable
    fta:  0.50,  // FT attempts: high variance (foul-drawing is streaky)
    threeAtt: 0.30, // 3PA: relatively stable
  };

  const cv = cvBase[statType] || 0.35;

  // OVR consistency modifier: 95 OVR → 0.80x variance, 70 OVR → 1.15x variance
  const ovrMod = 1.3 - (ovr / 100) * 0.5;

  // Volume modifier: higher averages → slightly tighter relative variance
  // A 28 PPG scorer won't have 0.32 CV, more like 0.28
  const volMod = avgStat > 15 ? 0.88 : avgStat > 8 ? 0.95 : 1.0;

  const sd = avgStat * cv * ovrMod * volMod;

  // Skew: scoring is right-skewed (explosion games), rebounds roughly symmetric
  const skew = statType === 'pts' ? 0.25 : statType === 'ast' ? 0.15 : 0;

  return { sd, skew };
}

// ============================================================================
// MINUTES VARIANCE
// ============================================================================
// Players don't always play their average minutes.
// Stars: tight (32-38 unless blowout/foul trouble)
// Rotation: moderate (12-28)
// Bench: wide (0-18)

function varyMinutes(avgMpg, ovr, isBlowout) {
  // Foul trouble chance (lower OVR = slightly higher)
  const foulTrouble = Math.random() < 0.06;
  // Rest/DNP chance for low-minutes guys
  const dnp = avgMpg < 8 && Math.random() < 0.25;

  if (dnp) return 0;

  let sd;
  if (avgMpg > 30) sd = 3.5;       // Stars: tight
  else if (avgMpg > 20) sd = 4.5;   // Starters
  else if (avgMpg > 12) sd = 5.0;   // Rotation
  else sd = 4.0;                     // Bench

  let mins = Math.round(randNormal(avgMpg, sd));

  if (foulTrouble) mins = Math.round(mins * rand(0.5, 0.75));
  if (isBlowout) mins = Math.round(mins * (avgMpg > 28 ? rand(0.7, 0.85) : rand(0.8, 1.3)));

  return clamp(mins, 0, 42);
}

// ============================================================================
// GENERATE ONE PLAYER'S BOX SCORE
// ============================================================================
// Takes: real season averages object + game context
// Returns: single-game stat line

function generatePlayerGame(playerAvg, gameContext) {
  const { teamFlow, isBlowout, overtimeMins } = gameContext;
  const ovr = getPlayerRating(playerAvg.name) || 75;
  const avg = playerAvg.stats; // The real season averages from API

  // Explicit DNP from depth chart
  if (playerAvg.isDNP) return createDNP(playerAvg.name);

  if (!avg || !avg.min) {
    // No stats available — DNP
    return null;
  }

  const avgMpg = parseFloat(avg.min) || 0;
  if (avgMpg < 1) return createDNP(playerAvg.name);

  // 1. Determine minutes for this game
  let minutes;
  if (playerAvg.isUserMinutes) {
    // User set these minutes explicitly — use them exactly (no variance)
    minutes = avgMpg;
  } else {
    minutes = varyMinutes(avgMpg, ovr, isBlowout);
    if (overtimeMins && avgMpg > 25) minutes = Math.min(48, minutes + randInt(2, 5));
  }
  if (minutes === 0) return createDNP(playerAvg.name);

  // Minutes ratio vs average (scales all counting stats)
  const minRatio = minutes / avgMpg;

  // 2. Game-level "hot/cold" factor — one roll that affects everything slightly
  //    This creates correlated games (hot night = more pts AND better shooting)
  const hotCold = randNormal(1.0, 0.08); // 0.84 to 1.16 range

  // 3. Team flow effect (good team chemistry game vs bad)
  const flow = teamFlow * hotCold;

  // 4. Generate each stat
  const genStat = (avgVal, statType, floor = 0) => {
    const base = (avgVal || 0) * minRatio * flow;
    const { sd, skew } = getVarianceProfile(avgVal || 0, statType, ovr, avgMpg);
    const scaledSd = sd * Math.sqrt(minRatio); // Variance scales with sqrt of time
    const raw = randSkewNormal(base, scaledSd, skew);
    return Math.max(floor, Math.round(raw));
  };

  // Field goals
  const fga = genStat(avg.fga, 'fga', 0);
  const fgPctVar = clamp(
    (avg.fg_pct || 0.43) + randNormal(0, 0.06) * (1.3 - ovr / 100),
    0.15, 0.80
  );
  const fgm = clamp(Math.round(fga * fgPctVar), 0, fga);

  // Three pointers
  const threeAtt = clamp(genStat(avg.fg3a, 'threeAtt', 0), 0, Math.round(fga * 0.8));
  const threePctVar = clamp(
    (avg.fg3_pct || 0.33) + randNormal(0, 0.08),
    0.0, 0.70
  );
  const threeMade = clamp(Math.round(threeAtt * threePctVar), 0, threeAtt);

  // Free throws
  const fta = genStat(avg.fta, 'fta', 0);
  const ftPctVar = clamp(
    (avg.ft_pct || 0.75) + randNormal(0, 0.05),
    0.30, 1.0
  );
  const ftm = clamp(Math.round(fta * ftPctVar), 0, fta);

  // Calculate actual points from shooting
  const pts = (fgm - threeMade) * 2 + threeMade * 3 + ftm;

  // Rebounds, assists, stocks
  const reb = genStat(avg.reb, 'reb', 0);
  const ast = genStat(avg.ast, 'ast', 0);
  const stl = genStat(avg.stl, 'stl', 0);
  const blk = genStat(avg.blk, 'blk', 0);
  const tov = genStat(avg.turnover, 'tov', 0);

  // Offensive/defensive rebound split (position-aware)
  const pos = playerAvg.position || 'SF';
  const orebPct = pos === 'C' ? 0.30 : pos === 'PF' ? 0.25 : 0.15;
  const oreb = Math.round(reb * clamp(orebPct + rand(-0.08, 0.08), 0, 0.5));
  const dreb = Math.max(0, reb - oreb);

  // Personal fouls
  const pf = clamp(Math.round(randNormal(2.2, 1.0) * (minutes / 30)), 0, 6);

  return {
    name: playerAvg.name,
    position: pos,
    minutes, pts, fgm, fga, threeMade, threeAtt, ftm, fta,
    reb, oreb, dreb, ast, stl, blk, tov, pf,
    plusMinus: 0, // Assigned after team scores known
    rating: ovr,
    isUserMinutes: !!playerAvg.isUserMinutes,
  };
}

function createDNP(name) {
  return {
    name, position: '', minutes: 0, pts: 0, fgm: 0, fga: 0,
    threeMade: 0, threeAtt: 0, ftm: 0, fta: 0,
    reb: 0, oreb: 0, dreb: 0, ast: 0, stl: 0, blk: 0, tov: 0, pf: 0,
    plusMinus: 0, rating: 0, dnp: true,
  };
}

// ============================================================================
// NORMALIZE TEAM MINUTES TO 240
// ============================================================================
function normalizeMinutes(box) {
  const active = box.filter(p => p && !p.dnp && p.minutes > 0);
  if (active.length === 0) return box;

  // Separate user-locked minutes from adjustable minutes
  const locked = active.filter(p => p.isUserMinutes);
  const adjustable = active.filter(p => !p.isUserMinutes);

  let total = active.reduce((s, p) => s + p.minutes, 0);
  const target = 240;
  let attempts = 0;

  // Only adjust NON-user-set players to hit 240
  while (total !== target && attempts < 80 && adjustable.length > 0) {
    const diff = target - total;
    const idx = randInt(0, adjustable.length - 1);
    const adj = diff > 0 ? 1 : -1;
    const newMin = adjustable[idx].minutes + adj;
    if (newMin >= 1 && newMin <= 48) {
      adjustable[idx].minutes = newMin;
      total += adj;
    }
    attempts++;
  }

  return box;
}

// ============================================================================
// NORMALIZE TEAM SCORE
// ============================================================================
// After generating individual stats, team total might be unrealistic.
// Nudge it toward a realistic NBA range by adjusting top scorers slightly.

function normalizeTeamScore(box, targetScore) {
  const active = box.filter(p => p && !p.dnp);
  const rawTotal = active.reduce((s, p) => s + p.pts, 0);

  if (rawTotal === 0 || rawTotal === targetScore) return box;

  const diff = targetScore - rawTotal;
  const scorers = active
    .filter(p => p.minutes > 15)
    .sort((a, b) => b.pts - a.pts);

  // Distribute the difference across top scorers
  let remaining = diff;
  for (let i = 0; i < scorers.length && remaining !== 0; i++) {
    const share = Math.round(remaining / (scorers.length - i));
    const newPts = Math.max(0, scorers[i].pts + share);
    const actualAdj = newPts - scorers[i].pts;
    scorers[i].pts = newPts;
    remaining -= actualAdj;
  }

  return box;
}

// ============================================================================
// COMPUTE PLUS/MINUS
// ============================================================================
function assignPlusMinus(box, teamMargin) {
  const active = box.filter(p => p && !p.dnp && p.minutes > 0);
  const totalMins = active.reduce((s, p) => s + p.minutes, 0) || 1;

  return box.map(p => {
    if (!p || p.dnp || p.minutes === 0) return p;
    const share = p.minutes / totalMins;
    const base = teamMargin * share * 2.5;
    const noise = randNormal(0, 5);
    return { ...p, plusMinus: Math.round(base + noise) };
  });
}

// ============================================================================
// MAIN: SIMULATE A FULL GAME
// ============================================================================
// playerStatsMap: { 'Player Name': { stats: apiSeasonAvgObj, position: 'PG' } }

export function simulateGame(homeTeam, awayTeam, homePlayers, awayPlayers) {
  if (!homePlayers?.length || !awayPlayers?.length) {
    return { error: 'Missing player data' };
  }

  // Team strength — weighted toward top players (stars carry teams)
  // Top 2 players weighted 3x, next 3 weighted 1.5x, rest weighted 1x
  const teamOvr = (players) => {
    const ovrs = players.map(p => getPlayerRating(p.name) || 70)
      .sort((a, b) => b - a).slice(0, 10);
    if (ovrs.length === 0) return 70;
    let weighted = 0, totalWeight = 0;
    for (let i = 0; i < ovrs.length; i++) {
      const w = i < 2 ? 3.0 : i < 5 ? 1.5 : 1.0;
      weighted += ovrs[i] * w;
      totalWeight += w;
    }
    return weighted / totalWeight;
  };

  const homeStr = teamOvr(homePlayers);
  const awayStr = teamOvr(awayPlayers);
  const homeAdv = 2.5; // Home court advantage in "points"
  const strengthDiff = (homeStr - awayStr + homeAdv);

  // Is this going to be a blowout? (affects minutes distribution)
  const blowoutChance = Math.abs(strengthDiff) > 8 ? 0.35 : 0.1;
  const isBlowout = Math.random() < blowoutChance;

  // Team-level flow factors (team chemistry, pace)
  // Slight boost for the better team
  const pace = rand(0.94, 1.06);
  const homeFlow = pace * (1 + strengthDiff * 0.003);
  const awayFlow = pace * (1 - strengthDiff * 0.003);

  // Generate box scores
  const homeContext = { teamFlow: homeFlow, isBlowout, overtimeMins: 0 };
  const awayContext = { teamFlow: awayFlow, isBlowout, overtimeMins: 0 };

  let homeBox = homePlayers
    .map(p => generatePlayerGame(p, homeContext))
    .filter(Boolean);
  let awayBox = awayPlayers
    .map(p => generatePlayerGame(p, awayContext))
    .filter(Boolean);

  // Normalize minutes to 240
  homeBox = normalizeMinutes(homeBox);
  awayBox = normalizeMinutes(awayBox);

  // Determine target scores
  const targetHome = clamp(
    Math.round(randNormal(112 + strengthDiff * 0.7, 7)),
    88, 142
  );
  const targetAway = clamp(
    Math.round(randNormal(112 - strengthDiff * 0.7, 7)),
    88, 142
  );

  // Normalize team totals toward target
  homeBox = normalizeTeamScore(homeBox, targetHome);
  awayBox = normalizeTeamScore(awayBox, targetAway);

  let homeScore = homeBox.filter(p => !p.dnp).reduce((s, p) => s + p.pts, 0);
  let awayScore = awayBox.filter(p => !p.dnp).reduce((s, p) => s + p.pts, 0);

  // Handle ties (overtime)
  let overtime = false;
  if (homeScore === awayScore) {
    overtime = true;
    const otWinner = Math.random() < 0.5 + strengthDiff * 0.02 ? 'home' : 'away';
    const otPts = randInt(2, 8);
    const box = otWinner === 'home' ? homeBox : awayBox;
    const star = box.filter(p => !p.dnp).sort((a, b) => b.pts - a.pts)[0];
    if (star) {
      star.pts += otPts;
      star.minutes = Math.min(48, star.minutes + 5);
    }
    if (otWinner === 'home') homeScore += otPts; else awayScore += otPts;
  }

  // Assign plus/minus
  const margin = homeScore - awayScore;
  homeBox = assignPlusMinus(homeBox, margin);
  awayBox = assignPlusMinus(awayBox, -margin);

  // Sort by minutes (starters first)
  homeBox.sort((a, b) => (b.minutes || 0) - (a.minutes || 0));
  awayBox.sort((a, b) => (b.minutes || 0) - (a.minutes || 0));

  // Quarter scores
  const genQuarters = (total) => {
    const qs = [0, 0, 0, 0];
    let rem = total;
    for (let i = 0; i < 3; i++) {
      qs[i] = clamp(Math.round(rem * rand(0.22, 0.29)), 18, 38);
      rem -= qs[i];
    }
    qs[3] = Math.max(15, rem);
    return qs;
  };

  return {
    homeTeam, awayTeam,
    homeScore, awayScore,
    winner: homeScore > awayScore ? homeTeam : awayTeam,
    loser: homeScore > awayScore ? awayTeam : homeTeam,
    margin: Math.abs(homeScore - awayScore),
    homeQuarters: genQuarters(homeScore),
    awayQuarters: genQuarters(awayScore),
    overtime,
    homeBox: homeBox.filter(p => !p.dnp),
    awayBox: awayBox.filter(p => !p.dnp),
    homeDNP: homeBox.filter(p => p.dnp),
    awayDNP: awayBox.filter(p => p.dnp),
    homeTeamStats: teamTotals(homeBox),
    awayTeamStats: teamTotals(awayBox),
    simDate: new Date().toISOString(),
  };
}

// ============================================================================
// TEAM TOTALS
// ============================================================================
function teamTotals(box) {
  const active = box.filter(p => p && !p.dnp);
  const sum = (k) => active.reduce((s, p) => s + (p[k] || 0), 0);
  const fgm = sum('fgm'), fga = sum('fga');
  const tm = sum('threeMade'), ta = sum('threeAtt');
  const ftm = sum('ftm'), fta = sum('fta');
  return {
    pts: sum('pts'), reb: sum('reb'), ast: sum('ast'),
    stl: sum('stl'), blk: sum('blk'), tov: sum('tov'),
    fgm, fga, fgPct: fga > 0 ? (fgm / fga * 100).toFixed(1) : '0.0',
    threeMade: tm, threeAtt: ta, threePct: ta > 0 ? (tm / ta * 100).toFixed(1) : '0.0',
    ftm, fta, ftPct: fta > 0 ? (ftm / fta * 100).toFixed(1) : '0.0',
    oreb: sum('oreb'), dreb: sum('dreb'),
  };
}

// ============================================================================
// QUICK SIM (W/L only, no box score — for bulk season sim)
// depthCharts optional: { 'MIN': { positions: { PG: [{name,minutes},...], ... }, dnp: [...] } }
// ============================================================================
const POS_IDX = { PG: 0, SG: 1, SF: 2, PF: 3, C: 4 };

function getOOPPenaltyInline(naturalPos, assignedPos) {
  if (!naturalPos || !assignedPos) return 0;
  const natPositions = (naturalPos || '').split('/').map(p => p.trim());
  let best = 99;
  for (const nat of natPositions) {
    const n = POS_IDX[nat], a = POS_IDX[assignedPos];
    if (n === undefined || a === undefined) { best = Math.min(best, 0); continue; }
    const d = Math.abs(n - a);
    const penalties = [0, 1, 3, 4, 5];
    best = Math.min(best, d < penalties.length ? penalties[d] : 5);
  }
  return -best;
}

export function quickSim(homeTeam, awayTeam, salaryData, depthCharts) {
  const getStrength = (abbr) => {
    const teamData = salaryData[abbr];
    const players = (teamData?.players || teamData || []);
    const playerArr = Array.isArray(players) ? players : [];
    const dc = depthCharts?.[abbr];

    if (dc?.positions) {
      // Use depth chart: weight by minutes, apply OOP penalty
      let totalWeight = 0, weightedOvr = 0;
      const POSITIONS = ['PG', 'SG', 'SF', 'PF', 'C'];
      for (const pos of POSITIONS) {
        for (const slot of (dc.positions[pos] || [])) {
          if (!slot?.name || !slot.minutes) continue;
          const baseOvr = getPlayerRating(slot.name) || 70;
          const naturalPos = PLAYER_POSITIONS[slot.name] || 'SF';
          const penalty = getOOPPenaltyInline(naturalPos, pos);
          const effectiveOvr = Math.max(40, baseOvr + penalty);
          weightedOvr += effectiveOvr * slot.minutes;
          totalWeight += slot.minutes;
        }
      }
      if (totalWeight > 0) return weightedOvr / totalWeight;
    }

    // Fallback: weighted by star power (top 2 count 3x, next 3 count 1.5x)
    const ratings = playerArr
      .filter(p => p.salary2526 > 0)
      .map(p => (p.ovr || getPlayerRating(p.name) || 70))
      .sort((a, b) => b - a)
      .slice(0, 10);
    if (ratings.length === 0) return 70;
    let wSum = 0, wTotal = 0;
    for (let i = 0; i < ratings.length; i++) {
      const w = i < 2 ? 3.0 : i < 5 ? 1.5 : 1.0;
      wSum += ratings[i] * w;
      wTotal += w;
    }
    return wSum / wTotal;
  };

  const homeStr = getStrength(homeTeam);
  const awayStr = getStrength(awayTeam);
  const diff = homeStr - awayStr + 2.5;

  // Win probability via logistic function — steeper for more realistic outcomes
  const winProb = 1 / (1 + Math.exp(-diff * 0.18));
  const homeWins = Math.random() < winProb;

  const marginAbs = Math.abs(Math.round(randNormal(Math.abs(diff) * 0.8, 8)));
  const basePts = Math.round(randNormal(112, 7));

  return {
    homeTeam, awayTeam,
    homeScore: homeWins ? basePts + Math.ceil(marginAbs / 2) : basePts - Math.floor(marginAbs / 2),
    awayScore: homeWins ? basePts - Math.floor(marginAbs / 2) : basePts + Math.ceil(marginAbs / 2),
    winner: homeWins ? homeTeam : awayTeam,
  };
}

// ============================================================================
// FALLBACK STAT ESTIMATION (when API stats unavailable)
// Generates synthetic averages from OVR rating + position
// ============================================================================
export function estimateAverages(playerName, position = 'SF', rosterIndex = 5, overrideMinutes = null) {
  const ovr = getPlayerRating(playerName) || 70;
  
  // Exponential OVR multiplier — stars score MUCH more than bench players
  // 95 OVR → 1.55, 90 → 1.32, 85 → 1.15, 80 → 1.0, 75 → 0.85, 72 → 0.75, 68 → 0.60
  const rMult = Math.pow(ovr / 80, 2.2);

  // Minutes based on roster slot (sorted by salary = role proxy)
  const minutesBySlot = [34, 33, 32, 30, 28, 22, 18, 15, 12, 8, 5, 3, 0];
  const baseMins = overrideMinutes !== null ? overrideMinutes : (minutesBySlot[Math.min(rosterIndex, 12)] || 0);

  // Scale stats to minutes (per-36 baseline, then ratio down)
  const minRatio = baseMins / 36;

  const profiles = {
    PG: { pts: 17, reb: 4.0, ast: 7.0, stl: 1.3, blk: 0.3, turnover: 2.8, fg_pct: 0.44, fg3_pct: 0.36, ft_pct: 0.84, fga: 15, fg3a: 5.5, fta: 3.5 },
    SG: { pts: 18, reb: 3.8, ast: 3.5, stl: 1.0, blk: 0.3, turnover: 2.0, fg_pct: 0.45, fg3_pct: 0.37, ft_pct: 0.83, fga: 16, fg3a: 6.0, fta: 3.2 },
    SF: { pts: 16, reb: 5.5, ast: 2.8, stl: 1.0, blk: 0.5, turnover: 1.8, fg_pct: 0.46, fg3_pct: 0.35, ft_pct: 0.80, fga: 14, fg3a: 4.0, fta: 3.0 },
    PF: { pts: 15, reb: 7.5, ast: 2.5, stl: 0.7, blk: 0.8, turnover: 1.6, fg_pct: 0.49, fg3_pct: 0.34, ft_pct: 0.77, fga: 12, fg3a: 3.0, fta: 3.0 },
    C:  { pts: 13, reb: 10.0, ast: 2.0, stl: 0.5, blk: 1.5, turnover: 1.5, fg_pct: 0.56, fg3_pct: 0.29, ft_pct: 0.72, fga: 10, fg3a: 1.2, fta: 3.0 },
  };

  const p = profiles[position] || profiles.SF;
  return {
    min: String(baseMins),
    pts: p.pts * rMult * minRatio,
    reb: p.reb * rMult * minRatio,
    ast: p.ast * rMult * minRatio,
    stl: p.stl * rMult * minRatio,
    blk: p.blk * rMult * minRatio,
    turnover: p.turnover * minRatio,
    fg_pct: p.fg_pct + (ovr - 75) * 0.002,   // Stars shoot slightly better
    fg3_pct: p.fg3_pct + (ovr - 75) * 0.001,
    ft_pct: p.ft_pct + (ovr - 75) * 0.001,
    fga: p.fga * rMult * minRatio,
    fg3a: p.fg3a * minRatio,
    fta: p.fta * rMult * minRatio,
    games_played: 60,
  };
}

export default { simulateGame, quickSim, estimateAverages };
